import React from 'react';
import { 
  Typography, 
  Grid, 
  Box, 
  Card, 
  CardMedia, 
  CardContent, 
  Container,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import NewsletterSignup from 'components/organisms/newsletterSignup/view';

// Icons
import PeopleIcon from '@mui/icons-material/People';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import AccessibilityIcon from '@mui/icons-material/Accessibility';

// Images
import heroImage from 'assets/images/about_us.jpeg';
import pedroImage from 'assets/images/team/pedro.jpg';
import alexImage from 'assets/images/team/alexander.jpg';

// Styled Components
const PageSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 2),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(5, 2),
  },
}));

const HeroSection = styled(PageSection)(() => ({
  background: 'linear-gradient(to bottom, rgba(255,255,255,0.9), rgba(255,255,255,1))',
  borderBottom: '1px solid rgba(0,0,0,0.1)',
  position: 'relative',
  overflow: 'hidden',
}));

const ValueCard = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  height: '100%',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'translateY(-5px)',
  },
}));

const TeamMemberCard = styled(Card)(({ theme }) => ({
  height: '100%',
  boxShadow: theme.shadows[2],
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[8],
  },
}));

const AboutUsView = () => {

  const values = [
    {
      title: 'Community First',
      description: 'Building meaningful connections in every space',
      icon: <PeopleIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
    {
      title: 'Innovation',
      description: 'Revolutionizing how people discover and share spaces',
      icon: <LightbulbIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
    {
      title: 'Accessibility',
      description: 'Making great spaces available to everyone',
      icon: <AccessibilityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
  ];

  const team = [
    {
      name: 'Pedro',
      role: 'CTO & Co-founder',
      bio: 'Seasoned CTO with expertise in workplace solutions and a passion for revolutionizing flexible working environments.',
      image: pedroImage,
    },
    {
      name: 'Alexander',
      role: 'CMO & Co-founder',
      bio: 'Visionary entrepreneur and CMO with more than 10 years of experience in startups and product innovation.',
      image: alexImage,
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>      
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h2" 
                gutterBottom 
                sx={{
                  fontSize: { xs: '2rem', md: '3rem' },
                  fontWeight: 700,
                  color: 'primary.main',
                  marginBottom: 3
                }}
              >
                Building Communities Through Shared Spaces
              </Typography>
              <Typography variant="h5" sx={{ color: 'text.secondary', mb: 4 }}>
                Connecting people, places, and possibilities
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: '100%',
                  height: '100%',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  zIndex: 0
                }
              }}>
                <img
                  src={heroImage}
                  alt="HiveSpace Community"
                  style={{ 
                    width: '100%', 
                    position: 'relative',
                    zIndex: 1,
                    borderRadius: '8px'
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      {/* Stats Section */}
      {/* <PageSection>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {stats.map((stat) => (
              <Grid item xs={12} sm={6} md={3} key={stat.label}>
                <StatsCard>
                  {stat.icon}
                  <Typography variant="h3" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                    {stat.number}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    {stat.label}
                  </Typography>
                </StatsCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </PageSection> */}

      {/* Mission Section */}
      <PageSection sx={{ bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h4" gutterBottom color="primary.main">
                Our Mission
              </Typography>
              <Typography variant="body1" paragraph>
                Welcome to <b>HiveSpace</b>, where community building meets real-world experiences. 
                Our mission is to empower individuals and organisations to create vibrant communities 
                and memorable events in the heart of London.
              </Typography>
              <Typography variant="body1">
                At HiveSpace, we believe in the power of face-to-face connections. Our platform 
                is designed to bring people together, fostering communities that thrive on shared 
                interests and experiences.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h4" gutterBottom color="primary.main">
                Our Vision
              </Typography>
              <Typography variant="body1" paragraph>
                We envision a world where finding and booking the perfect space for your community 
                or event is seamless and intuitive. Our platform seamlessly integrates vibrant 
                communities, genuine connections, and collaborative environments.
              </Typography>
              <Typography variant="body1">
                Whether creating communities, hosting events, or unlocking unique working spaces, 
                HiveSpace is your gateway to a transformative experience.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </PageSection>

      {/* Values Section */}
      <PageSection>
        <Container maxWidth="lg">
          <Typography variant="h4" gutterBottom align="center" sx={{ mb: 6 }}>
            Our Values
          </Typography>
          <Grid container spacing={4}>
            {values.map((value) => (
              <Grid item xs={12} md={4} key={value.title}>
                <ValueCard>
                  {value.icon}
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    {value.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {value.description}
                  </Typography>
                </ValueCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </PageSection>

      {/* Team Section */}
      <PageSection sx={{ bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" gutterBottom align="center" sx={{ mb: 6 }}>
            Meet Our Team
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {team.map((member) => (
              <Grid item xs={12} sm={6} md={4} key={member.name}>
                <TeamMemberCard>
                  <CardMedia
                    component="img"
                    height="240"
                    image={member.image}
                    alt={member.name}
                  />
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {member.name}
                    </Typography>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      {member.role}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {member.bio}
                    </Typography>
                  </CardContent>
                </TeamMemberCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </PageSection>

      {/* Newsletter Section */}
      <PageSection>
        <Container maxWidth="lg">
          <NewsletterSignup />
        </Container>
      </PageSection>

      
    </Box>
  );
};

export default AboutUsView;